import { CardContent, CardHeader, CardTitle, TransparentCard } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import PageWithNavbar from "@/components/PageWithNavbar";


interface GuideSection {
  id: string;
  title: string;
}

const guideSections: GuideSection[] = [
  {
    id: "charts",
    title: "Charts"
  },
  {
    id: "depth-of-book",
    title: "Depth of Book"
  },
  {
    id: "filters",
    title: "Filters"
  },
  {
    id: "algorithms",
    title: "Algorithms"
  },
  {
    id: "news-feed",
    title: "News Feed"
  }
];

const renderGuideContent = (guide: GuideSection) => {
  switch (guide.id) {
    case 'depth-of-book':
      return (
        <div className="prose dark:prose-invert max-w-none">
          <b>What is Depth of Book?</b>
          <p>
            Depth of book is a display of the true market. In Doyen it is divided into two grids: bids on the left and offers (or 'asks') on the right. Each row in the grid is a bid or offer from an exchange, and in Doyen (unlike anything else out there for cryptocurrency) you will find the bids and offers of every exchange it supports. By default, Doyen sorts bids from highest to lowest and offers from lowest to highest.
          </p>

          <p>
            To translate this into something more concrete, let's take the example of going into a marketplace to buy salmon. Like cryptocurrency, let's assume all salmon at this marketplace are exactly the same. Like in Doyen, there may be several vendors, and let's also assume there are several other buyers like yourself.
          </p>
          
          <p>
            When you arrive at the market the vendors announce the price for the salmon they're selling. These prices are the offers, or the 'asks', as the vendors are <em>asking</em> you to pay, or <em>offering</em> you the salmon, at that price. Likewise, perhaps we have some stubborn buyers who are not willing to agree to the vendors' offers. They, then, announce the price they're willing to buy at. These are the 'bids', as they're making a bid that a vendor will come down and agree to their price.
          </p>
          
          <p>
            These two together, the vendors offers and the buyers bids, form the 'book' for salmon at that marketplace. If there are multiple prices amongst the vendors and the buyers, each unique price would have its own row giving the book 'depth'. Thus, we have a depth of book.
          </p>
          
          <p>
            Every symbol you will find in Doyen will have a depth of book. Some may only be traded on certain exchanges, which should be accounted for by customizing the symbol's settings. For completeness, here is common terminology regarding depth of book:
          </p>
          
          <ol>
            <li><strong>Top of Book:</strong> A depth of one (Doyen's default) provides the best bid and best offer from the exchange.</li>
            <li><strong>Spread:</strong> The difference between the best bid and best offer.</li>
            <li><strong>BBO:</strong> Best Bid or Offer. Shorthand for the best possible price relative to an order. For example, a bid at $1, I place an order at $1.01, I am 1 cent from (or 'off of') BBO.</li>
            <li><strong>NBBO:</strong> Doyen provides access to NBBO, as we provide access to data from across US exchanges. The N stands for National. For example, Kraken is bid at $1, Gemini is bid at $1.05, Gemini is the 'national best bid' because it provides the best bid across all available exchanges.</li>
          </ol>
 
          <b id="dob-interactive">Interactivity</b>
          <p>
            Doyen provides interactive functions in its depth of book, which are all customizable on a per symbol basis:
          </p>
          
          <ol>
            <li>
              <strong>Copy Price</strong><br />
              Copies the price of a level when clicked. Useful for quickly setting the price to NBBO.
            </li>
            <li>
              <strong>Copy Asset Size</strong><br />
              Copies the asset size of a level when clicked. Useful for large orders, pushing, and taking from the book -- especially when paired with a condense option.
            </li>
            <li>
              <strong>Cancel Orders</strong><br />
              Orders are automatically displayed on the Depth of Book. This option cancels the corresponding order when clicked.
            </li>
          </ol>

            <div className="w-full bg-muted/50 rounded-lg p-4 text-center">
                <video 
                controls
                autoPlay 
                loop 
                muted 
                playsInline
                className="w-full mx-auto rounded-lg"
                >
                    <source src="/Dob-Copyprice.webm" type="video/webm" />
                    Your browser does not support the video tag.
                </video>
            </div>

          <br />

          <b id="dob-customization">Customization</b>
          <p>
            Depth Levels is the maximum number of levels to display per exchange.
          </p>
          
          <p>
            Along with Depth Levels -- and filters which is covered in the Filters Guide -- Doyen provides access to condensing the book. Each condense option is paired with a quantity value. When both are selected, you should see the empty circle next to quantity fill with a checkmark.
          </p>
          
          <p>There are three options for condensing:</p>
          
          <ol>
            <li>
              <strong>Asset Size</strong><br />
              Condenses the book down such that each level is at most the quantity of the base asset. For example, BTC-USDT with quantity 1.0. Each level will be at most 1.0 BTC.
            </li>
            <li>
              <strong>Price Increment</strong><br />
              Condenses the book into price increments, initially based on the BBO price. For example, BTC-USDT with quantity 100. If Gemini is bid at $100,052 then levels will be condensed between $100,052-$100,152, $100,152-$100,252, etc. Price increments are skipped if there are no levels within the price increment.
            </li>
            <li>
              <strong>Notional</strong><br />
              Condenses the book by notional value. For example, BTC-USDT with quantity 100. If Coinbase has its best bid with a notional of $72 and its second best with a notional of $128, you will see two levels. The first a weighted average price for the $100 notional value, and the second the price of the (originally) $128 bid.
            </li>
          </ol>

            <div className="w-full bg-muted/50 rounded-lg p-4 text-center">
                <video 
                controls
                autoPlay 
                loop 
                muted 
                playsInline
                className="w-full mx-auto rounded-lg"
                >
                    <source src="/Dob-Custom-Size.webm" type="video/webm" />
                    Your browser does not support the video tag.
                </video>
            </div>

          <br />
          
          <p>
            Prices for condensed levels are always the weighted average across levels that were condensed. If, for example, we're condensing by an Asset Size of 1.0 and there was 0.5 bid at $100k and 0.5 bid at $99k; the price for the condensed level will display $99.5k.
          </p>
          <hr />
        </div>
      );
    
    case 'filters':
      return (
        <div className="prose dark:prose-invert max-w-none">
          <p>
            Filters are the fastest way to trim down to what you want to see in Doyen. Currently, all filters are joined by an AND -- so whatever filters you enter must have all of their conditions met or they will not be shown.
          </p>

          <br />

          <b>Setting a Filter</b>
          <p>
            You can set a filter by first clicking the Filter icon on to the left of any grid. This will pop out the Filter menu. Type a property name (any column name in the grid) and click the plus button. You can then provide the criteria, with the central dropdown selecting the condition (equals, doesn't equal, between, and so on) and the second being the criteria to match.
          </p>

            <div className="w-full bg-muted/50 rounded-lg p-4 text-center">
                <video 
                controls
                autoPlay 
                loop 
                muted 
                playsInline
                className="w-full mx-auto rounded-lg"
                >
                    <source src="/Filters-Customize.webm" type="video/webm" />
                    Your browser does not support the video tag.
                </video>
            </div>

          <br />

          <b>Clearing a Filter</b>
          <p>
            Clearing a filter is as simple as clicking the Clear button.
          </p>

          <br />

          <b>Are my filters saved?</b>
          <p>
            Yes, along with the rest of your configurations, your filters will be saved when you exit Doyen. If you find data missing from a grid, such as an entire exchange, check your filters to see if you mistakenly left one on.
          </p>
          <hr />
        </div>
      );
    
    case 'charts':
      return (
        <div className="prose dark:prose-invert max-w-none">
          <p>
            The charts in Doyen are highly customizable and allow for arbitrary indicators to be added. Below we'll outline the various functionalities and how to use them.
          </p>
          
          <p><b>Interactivity</b></p>
          <p><i>Hotkeys</i></p>
          <p>
            Currently, the hotkeys for chart interaction are not customizable. Here are the hotkeys for navigating the chart:
          </p>
          
          <ul>
            <li><strong>Zoom:</strong> Scroll</li>
            <li><strong>Zoom (Time Axis):</strong> Ctrl + Scroll</li>
            <li><strong>Zoom (Price Axis):</strong> Shift + Scroll</li>
            <li><strong>Area Selection:</strong> Hold Alt and select an area.</li>
          </ul>

          <p><i>Right-Click Menu</i></p>
          <p>
            Right-clicking on a chart will bring up a context menu with several options for interacting with the chart. These options include:
          </p>
          <ul>
            <li><strong>Add:</strong> Opens a submenu with options to add indicators to the chart. For Premium users, you also add a chart to the layout.</li>
            <li><strong>Remove:</strong> Opens a submenu with options to remove indicators from the chart. For Premium users, you also remove a chart from the layout.</li>
            <li><strong>Rename:</strong> Allows you to rename the chart.</li>
            <li><strong>Reset Zoom:</strong> Resets the zoom level to the default view.</li>
          </ul>

          <p><i>Multi-Chart</i></p>
          <p>
            For Premium users, Doyen supports multi-chart layouts. As outlined above, by right-clicking on a chart you can add or remove charts from the layout. Once multiple charts are present, you can drag and drop them to rearrange their order.
          </p>

            <div className="w-full bg-muted/50 rounded-lg p-4 text-center">
                <video 
                controls
                autoPlay 
                loop 
                muted 
                playsInline
                className="w-full mx-auto rounded-lg"
                >
                    <source src="/Multichart.webm" type="video/webm" />
                    Your browser does not support the video tag.
                </video>
            </div>

          <br />
          
          <i>Indicators</i>
          <p>
            Indicators can be started by opening the Settings expander, selecting the indicator from the Indicator dropdown and clicking the Start button. Indicators can be interacted with by clicking on their name below the chart and modifying their settings. You can restart or stop (remove) an indicator from this interface.
          </p>

            <div className="w-full bg-muted/50 rounded-lg p-4 text-center">
                <video 
                controls
                autoPlay 
                loop 
                muted 
                playsInline
                className="w-full mx-auto rounded-lg"
                >
                    <source src="/Charts-Customize-Indicator.webm" type="video/webm" />
                    Your browser does not support the video tag.
                </video>
            </div>

          <br />
          
          <b>Customization</b>
          <p>
            There are three sections of customizability for charting:
          </p>
          
          <ul>
            <li>
              <strong>Settings Expander:</strong> You can select the timeframe, specify the exchange and add indicators from this menu.
            </li>

            <div className="w-full bg-muted/50 rounded-lg p-4 text-center">
                <video 
                controls
                autoPlay 
                loop 
                muted 
                playsInline
                className="w-full mx-auto rounded-lg"
                >
                    <source src="/Charts-New-Indicator.webm" type="video/webm" />
                    Your browser does not support the video tag.
                </video>
            </div>

            <li>
                <strong>Settings View:</strong> In the Settings view you're able to toggle whether Doyen should use the Price Display script or not for rendering candlestick data. Disabling the scripted display may slightly improve performance, but customization will be lost -- candles will be red when close is lower than open and green when close is higher than open.
            </li>

          <br />
            <li>
              <strong>Symbols Settings:</strong> In the Symbols settings menu, you'll find the "Average High and Low" for charts. This will take the average high and average low across exchanges when enabled, or get the highest across exchanges and lowest across exchanges when disabled. You'll see much larger wicks when this is disabled, but have a better picture of the range the symbol is trading in across the market.
            </li>
          </ul>
          <hr />
        </div>
      );
    
    case 'algorithms':
      return (
        <div className="prose dark:prose-invert max-w-none">
          <p>
            The Algorithms view provides a simple interface for running your algorithmic trading strategies. It allows for running any number of concurrent algorithms alongside a dashboard tracking its open and historical orders, data feeds subscribed to and other metrics that may be useful in monitoring.
          </p>

          <br />
          
          <b>How do I start an algorithm?</b>
          <p>
            Out of the box, we include (at the time of writing this) one algorithm example for a grid trader. Here is how to start it up:
          </p>
          
          <ol>
            <li>Navigate to the Algorithms view from the left-side panel.</li>
            <li>Select GridTrader from the dropdown on the top left of the view.</li>
            <li>You should see the algorithm added to the display grid with its state being "Initialized." You can customize the algorithm's settings to match what and how aggressively you would like it to trade.</li>
            <li>Click Start or, if you click away, right-click the algorithm and select Start.</li>
          </ol>
          
          <p>The algorithm will now be running live.</p>

            <div className="w-full bg-muted/50 rounded-lg p-4 text-center">
                <video 
                controls
                autoPlay 
                loop 
                muted 
                playsInline
                className="w-full mx-auto rounded-lg"
                >
                    <source src="/Algorithms-Start.webm" type="video/webm" />
                    Your browser does not support the video tag.
                </video>
            </div>

          <br />
          
          <b>How do I shut down an algorithm?</b>
          <p>
            To shut down a running algorithm first right-click it and select Pause, then right-click it again and select Stop. Pause will disable the algorithms ability to send opening orders, so while its paused you will not have to worry about new positions being opened by the algorithm. Stop fully shuts down the algorithm. For a better understanding of why the shutdown process is two steps, reference the below light introduction to how Doyen's algorithms work.
          </p>
          

            <div className="w-full bg-muted/50 rounded-lg p-4 text-center">
                <video 
                controls
                autoPlay 
                loop 
                muted 
                playsInline
                className="w-full mx-auto rounded-lg"
                >
                    <source src="/Algorithms-Stop.webm" type="video/webm" />
                    Your browser does not support the video tag.
                </video>
            </div>

          <br />
          
          <b>Algorithms in Doyen</b>
          <p>
            Algorithms in Doyen work off of a state machine. There are four states: Initialized, Running, Paused and Stopped. Initialized indicates that the scripting engine host has readied the algorithm to be started or for configuration. Running indicates the algorithm is live, processing data, and able to execute trades. Paused, critically, is a state in which the algorithm is still receiving data but cannot create orders that would open a new position -- in essence, in the paused state your algorithm will receive data and close positions, but will not open new ones. Thus, you have the ability to monitor, debug or analyze the algorithm. The Stop state indicates the algorithm has been removed from the scripting host, along with closing its data feeds and naturally its ability to send orders -- this is the true "shut down" state.
          </p>
        <hr />
        </div>
      );
    
    case 'news-feed':
      return (
        <div className="prose dark:prose-invert max-w-none">
          <p>
            The News Feed is a continuous feed, displaying news as it is received by our servers. The news feed has two additional properties to normal news feeds, in that it utilizes specialized AI models for analyzing the article's sentiment and for summarizing the article in order to quickly tell you what it's saying.
          </p>

          <br />

          <b>How can I filter the news feed?</b>
          <p>
            The news feed can be filtered by asset. Simply type an asset into the search bar and click the filter icon.
          </p>

            <div className="w-full bg-muted/50 rounded-lg p-4 text-center">
                <video 
                controls
                autoPlay 
                loop 
                muted 
                playsInline
                className="w-full mx-auto rounded-lg"
                >
                    <source src="/Newsfeed-Filtering.webm" type="video/webm" />
                    Your browser does not support the video tag.
                </video>
            </div>
          <hr />
        </div>
      );
    
    default:
      return (
        <div className="prose dark:prose-invert max-w-none">
          <h3>Getting Started with {guide.title}</h3>
          <p>
            This section will contain detailed instructions for using {guide.title.toLowerCase()} 
            features in Doyen Trading Platform. The content will be populated with step-by-step 
            guides, screenshots, and best practices.
          </p>

          <h4>Prerequisites</h4>
          <p>
            Before following this guide, ensure you have:
          </p>
          <ul>
            <li>Active Doyen Trading Platform account</li>
            <li>Connected exchange accounts (see <a href="/setup" className="text-primary hover:underline">Setup Guide</a>)</li>
            <li>Basic understanding of cryptocurrency trading concepts</li>
          </ul>

          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg mt-6">
            <h4 className="text-blue-800 dark:text-blue-200 mb-2">💡 Pro Tip</h4>
            <p className="text-blue-700 dark:text-blue-300 text-sm">
              Detailed step-by-step instructions will be added here. This placeholder 
              demonstrates the structure and layout for each guide section.
            </p>
          </div>
        </div>
      );
  }
};

export const UserManualPage = () => {
  const navigate = useNavigate();

  // Handle URL hash navigation on component mount
  useEffect(() => {
    const handleHashNavigation = () => {
      const hash = window.location.hash.slice(1); // Remove the # symbol
      if (hash) {
        // Small delay to ensure the DOM is fully rendered
        setTimeout(() => {
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    };

    // Handle hash on initial load
    handleHashNavigation();

    // Handle hash changes (if user navigates with hash)
    window.addEventListener('hashchange', handleHashNavigation);

    return () => {
      window.removeEventListener('hashchange', handleHashNavigation);
    };
  }, []);

  return (
    <PageWithNavbar>
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-3xl md:text-4xl font-bold">
            User{" "}
            <span className="bg-gradient-to-b from-primary/60 to-primary text-transparent bg-clip-text">
              Manual
            </span>          
          </h1>          
          <p className="text-xl text-muted-foreground mt-2 mb-6">
            Comprehensive guide of the Doyen Trading Platform.
          </p>

          {/* Quick Navigation */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4">Jump to Guide:</h3>
            <div className="flex flex-wrap gap-3">
              {guideSections.map((guide) => (
                <Button
                  key={guide.id}
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const element = document.getElementById(guide.id);
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                  className="flex items-center gap-2"
                >
                  {guide.title}
                </Button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid gap-8">
          {guideSections.map((guide, index) => (
            <TransparentCard key={index} id={guide.id} className="border-2 scroll-mt-24">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div>
                      <CardTitle className="text-xl">{guide.title}</CardTitle>
                    </div>
                  </div>
                </div>
              </CardHeader>

              <CardContent>
                <div className="space-y-6">
                  {/* Guide Content */}
                  {renderGuideContent(guide)}
                </div>
              </CardContent>
            </TransparentCard>
          ))}
        </div>

        {/* Call to Action */}
        <section className="text-center mt-12">
          <TransparentCard className="bg-primary/5 p-8">
            <h3 className="text-2xl font-bold mb-4">
              Need Additional Help?
            </h3>
            <p className="text-muted-foreground mb-6">
              Can't find what you're looking for? Check out our setup guides or contact our support team.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate("/support")}
                className="bg-primary hover:bg-primary/90"
              >
                Doyen Support
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate("/setup")}
              >
                Exchange Setup
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate("/roadmap")}
              >
                Feature Roadmap
              </Button>
            </div>
          </TransparentCard>
        </section>
      </div>
    </div>
    </PageWithNavbar>
  );
};

export default UserManualPage;
